import { apiClient } from './client.ts';
import { AnalyticsData, PerformanceMetricsData, Strategy, TimeSeriesPoint } from '../types/analytics.ts';

// Generate dates for the last 7 days
const generateDates = (days: number = 7) => {
  return Array.from({ length: days }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (days - 1 - i));
    return date.toISOString().split('T')[0];
  });
};

// Generate random scores
const generateScores = (length: number = 7, base: number = 50, variance: number = 30) => {
  return Array.from({ length }, () => Math.floor(base + Math.random() * variance));
};

// Generate time series data
const generateTimeSeries = (days: number = 30, base: number = 50, variance: number = 30): TimeSeriesPoint[] => {
  return Array.from({ length: days }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (days - 1 - i));
    return {
      timestamp: date.toISOString().split('T')[0],
      value: Math.floor(base + Math.random() * variance),
    };
  });
};

// Generate SARIMA components
const generateSARIMAComponents = (length: number = 30) => {
  const trend = Array.from({ length }, (_, i) => 50 + Math.sin(i / 5) * 10);
  const seasonal = Array.from({ length }, (_, i) => Math.sin(i / 7) * 5);
  const residuals = Array.from({ length }, () => (Math.random() - 0.5) * 3);
  return { trend, seasonal, residuals };
};

// Generate forecast data
const generateForecast = (days: number = 7): TimeSeriesPoint[] => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() + 1);
  
  return Array.from({ length: days }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return {
      timestamp: date.toISOString().split('T')[0],
      value: 75 + Math.sin(i / 3) * 15 + (Math.random() - 0.5) * 5,
    };
  });
};

// Mock data
const mockPerformanceMetrics: PerformanceMetricsData = {
  timestamps: generateDates(),
  optimizationScores: generateScores(),
  stabilityScores: generateScores(),
  driftScores: generateScores(),
  currentOptimizationScore: 85,
  averageStability: 92,
  driftRate: 5,
  efficiency: 85,
  accuracy: 92,
  latency: 120,
  throughput: 1000,
  costEfficiency: 78,
  // Time series data
  performanceHistory: generateTimeSeries(30, 80, 20),
  efficiencyTrend: generateTimeSeries(30, 85, 15),
  accuracyTrend: generateTimeSeries(30, 90, 10),
  // SARIMA components
  seasonalPeriod: 7,
  trendComponent: generateSARIMAComponents(30).trend,
  seasonalComponent: generateSARIMAComponents(30).seasonal,
  residuals: generateSARIMAComponents(30).residuals,
  forecast: generateForecast(),
  confidenceIntervals: {
    upper: generateScores(7, 90, 10),
    lower: generateScores(7, 60, 10),
  },
};

const mockStrategies: Strategy[] = [
  {
    id: '1',
    name: 'Neural Network Optimization',
    description: 'Optimize neural network architecture for performance',
    performance: 85,
    isActive: true,
    historicalPerformance: generateTimeSeries(30, 85, 15),
    impactScore: 92,
    confidenceScore: 88,
    lastUpdated: new Date().toISOString(),
  },
  {
    id: '2',
    name: 'Data Pipeline Enhancement',
    description: 'Improve data processing pipeline efficiency',
    performance: 72,
    isActive: false,
    historicalPerformance: generateTimeSeries(30, 72, 20),
    impactScore: 78,
    confidenceScore: 82,
    lastUpdated: new Date().toISOString(),
  },
  {
    id: '3',
    name: 'Resource Allocation Strategy',
    description: 'Optimize resource distribution across components',
    performance: 90,
    isActive: true,
    historicalPerformance: generateTimeSeries(30, 90, 10),
    impactScore: 95,
    confidenceScore: 94,
    lastUpdated: new Date().toISOString(),
  },
];

const mockAnalyticsData: AnalyticsData = {
  performanceMetrics: mockPerformanceMetrics,
  strategies: mockStrategies,
  lastAnalysisDate: new Date().toISOString(),
  nextUpdateScheduled: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  modelAccuracy: 94,
  anomalyDetection: {
    anomalies: generateTimeSeries(5, 40, 60),
    severity: ['low', 'medium', 'high', 'medium', 'low'],
  },
};

export const fetchAnalyticsData = async (): Promise<AnalyticsData> => {
  try {
    const response = await apiClient.get('/api/analytics');
    return response.data;
  } catch (error) {
    // Fallback to mock data if API call fails
    return mockAnalyticsData;
  }
};

export const fetchPerformanceMetrics = async (timeRange: string): Promise<PerformanceMetricsData> => {
  try {
    const response = await apiClient.get(`/api/analytics/performance?range=${timeRange}`);
    return response.data;
  } catch (error) {
    // Fallback to mock data if API call fails
    return mockPerformanceMetrics;
  }
};

export const fetchOptimizationStrategies = async (): Promise<Strategy[]> => {
  try {
    const response = await apiClient.get('/api/analytics/strategies');
    return response.data;
  } catch (error) {
    // Fallback to mock data if API call fails
    return mockStrategies;
  }
};

export const updateStrategy = async (strategyId: string, data: Partial<Strategy>): Promise<{ success: boolean }> => {
  try {
    const response = await apiClient.put(`/api/analytics/strategies/${strategyId}`, data);
    return response.data;
  } catch (error) {
    // Fallback to mock response if API call fails
    return { success: true };
  }
}; 